import React, { useState } from "react";
import aiCardBottom from "../../../assets/landing-page/ai-card-bottom.png";
import demoCardBottom from "../../../assets/landing-page/demo-card-bottom.png";
import AnimateFromBottom from "../../LandingPage/component/AnimateFromBottom";
import language from "../../../helpers/language";

const OutlineButton = ({ children, onClick, as: Tag = "button", ...rest }) => (
  <Tag
    onClick={onClick}
    className="learn-more-link hover:text-black inline-flex items-center gap-3 bg-white border border-gray-200 hover:border-gray-300 text-gray-900 font-semibold text-sm px-5 py-2 rounded-md transition-colors shadow-sm"
    {...rest}
  >
    <span>{children}</span>
    <span className="learn-more-link__icon w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
      <i className="fa fa-arrow-right text-white text-xs" />
    </span>
  </Tag>
);

// Bottom-right artwork for the AI card (Figma export).
const TealSparkle = () => (
  <>
    <img
      src={aiCardBottom}
      alt=""
      aria-hidden="true"
      className="absolute pointer-events-none select-none"
      style={{
        right: "-2px",
        bottom: "0px",
        width: "560px",
        maxWidth: "92%",
        zIndex: 1,
      }}
    />
    {/* Small floating sparkle — top-right, above the circle */}
    <svg
      style={{ position: "absolute", top: "38px", right: "28px", zIndex: 2, display: "none" }}
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
    >
      <path
        d="M22 2 L25.5 18.5 L42 22 L25.5 25.5 L22 42 L18.5 25.5 L2 22 L18.5 18.5 Z"
        fill="rgba(180,238,222,0.92)"
      />
    </svg>

    {/* Large teal circle + 3D star — bottom-right, partially clipped */}
    <div
      style={{
        position: "absolute",
        display: "none",
        right: "-70px",
        bottom: "-70px",
        width: "300px",
        height: "300px",
        borderRadius: "50%",
        background: "linear-gradient(140deg, #31b98a 0%, #3dcba2 45%, #28a87c 100%)",
        zIndex: 1,
      }}
    >
      {/* 3D thick star SVG centered on circle */}
      <svg
        style={{ position: "absolute", top: "42px", left: "52px" }}
        width="170"
        height="170"
        viewBox="0 0 170 170"
        fill="none"
      >
        {/* Outer filled star — semi-transparent teal layer (shadow/depth) */}
        <path
          d="M85 10 L100 75 L165 85 L100 95 L85 160 L70 95 L5 85 L70 75 Z"
          fill="rgba(25,140,100,0.55)"
          strokeLinejoin="round"
        />
        {/* Mid star — slightly inset */}
        <path
          d="M85 28 L97 76 L145 85 L97 94 L85 142 L73 94 L25 85 L73 76 Z"
          fill="rgba(30,160,115,0.4)"
          strokeLinejoin="round"
        />
        {/* Bright white/cyan inner outline — the glowing edge */}
        <path
          d="M85 10 L100 75 L165 85 L100 95 L85 160 L70 95 L5 85 L70 75 Z"
          fill="none"
          stroke="rgba(255,255,255,0.75)"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        {/* Inner bright outline for 3D depth */}
        <path
          d="M85 35 L96 76 L137 85 L96 94 L85 135 L74 94 L33 85 L74 76 Z"
          fill="none"
          stroke="rgba(180,255,230,0.6)"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  </>
);

const AlyseiAI = () => {
  const [lang] = useState(language.getLang());

  const focusAssistantSearch = () => {
    const input = document.getElementById("ai-assistant-search-input");

    if (input) {
      input.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => input.focus(), 450);
      return;
    }

    window.location.href = "/#ai-assistant-search";
  };

  return (
    <section className="md:px-14 px-4 md:pt-8 md:pb-14 py-8 bg-white">
      <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">

        {/* AI Card */}
        <AnimateFromBottom delay={0.2}>
          <div className="relative rounded-xl border border-gray-200 bg-white px-8 pt-8 pb-20 overflow-hidden" style={{ minHeight: "430px" }}>
            <TealSparkle />

            <div className="relative z-10" style={{ maxWidth: "65%" }}>
              <p className="text-sm font-bold text-green-accent tracking-wider mb-3">
                * {lang?.ai_agent} *
              </p>
              <h3 className="text-base md:text-xl font-extrabold text-gray-900 leading-tight mb-6">
                {lang?.ai_section_text}
              </h3>

              <OutlineButton type="button" onClick={focusAssistantSearch}>
                {lang?.try_now}
              </OutlineButton>
            </div>

            {/* Floating pill — bottom left, stays above teal */}
            <div className="absolute left-8 z-10" style={{ bottom: "54px" }}>
              <div
                style={{
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  background: "rgba(255,255,255,0.35)",
                  border: "1px solid rgba(255,255,255,0.55)",
                  borderRadius: "999px",
                  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.08)",
                  padding: "10px 18px 10px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  maxWidth: "260px",
                  minWidth: "220px",
                }}
              >
                <span style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="16.6038" cy="16.6038" r="16.6038" fill="#37A282"/>
                    <path d="M16.1659 10.3103C16.1965 10.1465 16.2834 9.99864 16.4116 9.89222C16.5397 9.7858 16.701 9.72754 16.8676 9.72754C17.0342 9.72754 17.1955 9.7858 17.3236 9.89222C17.4518 9.99864 17.5387 10.1465 17.5692 10.3103L18.3194 14.2775C18.3727 14.5595 18.5098 14.819 18.7128 15.0219C18.9157 15.2249 19.1752 15.362 19.4572 15.4153L23.4244 16.1655C23.5882 16.196 23.7361 16.2829 23.8425 16.4111C23.9489 16.5392 24.0072 16.7005 24.0072 16.8671C24.0072 17.0337 23.9489 17.195 23.8425 17.3231C23.7361 17.4513 23.5882 17.5382 23.4244 17.5688L19.4572 18.3189C19.1752 18.3722 18.9157 18.5093 18.7128 18.7123C18.5098 18.9152 18.3727 19.1747 18.3194 19.4567L17.5692 23.4239C17.5387 23.5877 17.4518 23.7356 17.3236 23.842C17.1955 23.9484 17.0342 24.0067 16.8676 24.0067C16.701 24.0067 16.5397 23.9484 16.4116 23.842C16.2834 23.7356 16.1965 23.5877 16.1659 23.4239L15.4158 19.4567C15.3625 19.1747 15.2254 18.9152 15.0224 18.7123C14.8195 18.5093 14.56 18.3722 14.278 18.3189L10.3108 17.5688C10.147 17.5382 9.99913 17.4513 9.89271 17.3231C9.78628 17.195 9.72803 17.0337 9.72803 16.8671C9.72803 16.7005 9.78628 16.5392 9.89271 16.4111C9.99913 16.2829 10.147 16.196 10.3108 16.1655L14.278 15.4153C14.56 15.362 14.8195 15.2249 15.0224 15.0219C15.2254 14.819 15.3625 14.5595 15.4158 14.2775L16.1659 10.3103Z" stroke="white" strokeWidth="1.42757" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22.5781 9.72925V12.5844" stroke="white" strokeWidth="1.42757" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M24.0055 11.1567H21.1504" stroke="white" strokeWidth="1.42757" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M11.1571 24.0055C11.9455 24.0055 12.5846 23.3664 12.5846 22.578C12.5846 21.7895 11.9455 21.1504 11.1571 21.1504C10.3686 21.1504 9.72949 21.7895 9.72949 22.578C9.72949 23.3664 10.3686 24.0055 11.1571 24.0055Z" stroke="white" strokeWidth="1.42757" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <p style={{ fontSize: "10px", fontWeight: 400, color: "#1a1a1a", lineHeight: "1.4", margin: 0 }}>
                  Find me wine producers in Tuscany for U.S. distribution
                </p>
              </div>
            </div>
          </div>
        </AnimateFromBottom>

        {/* Demo Card */}
        <AnimateFromBottom delay={0.3}>
          <div className="relative rounded-xl border border-gray-200 bg-white overflow-hidden" style={{ minHeight: "430px" }}>
            {/* Text — full width top section */}
            <div className="relative z-10 px-8 pt-8 pb-6">
              <p className="text-sm font-bold text-green-accent tracking-wider mb-3">
                {lang?.get_a_demo}
              </p>
              <h3 className="text-xl md:text-2xl font-extrabold text-gray-900 leading-tight mb-3">
                {lang?.How_can_we_help}
              </h3>
              <p className="text-sm text-gray-500 leading-snug mb-5" style={{ maxWidth: "77%" }}>
                {lang?.Schedule_Demo_Description}
              </p>

              <OutlineButton
                as="a"
                href="https://calendly.com/alysei2024"
                target="_blank"
                rel="noopener noreferrer"
              >
                {lang?.Schedule_Demo}
              </OutlineButton>
            </div>

            {/* Demo mockup artwork — bottom-right, partially overflowing */}
            <img
              src={demoCardBottom}
              alt="Alysei platform mockup"
              className="absolute pointer-events-none select-none"
              style={{
                right: "10px",
                bottom: "-10px",
                width: "520px",
                maxWidth: "72%",
                maxHeight: "240px",
                zIndex: 1,
              }}
            />
          </div>
        </AnimateFromBottom>

      </div>
    </section>
  );
};

export default AlyseiAI;
